// src/components/Refunds.js
// Remboursements — /refunds. The money of carrier claims and customer
// refunds (KI-158): what was granted, what is already paid, what is still to
// collect, what waits for the carrier's decision — over a period the user
// picks, from GET /api/logistics/refunds/stats (refundsMoney.js derives the
// numbers). « À vérifier » lists the granted refunds whose amount is missing;
// the « Dossiers » view is the filtered list, « Répartition » the money by
// status, by carrier, by client and what it covers. Filters, period, view and
// page live in the URL (Back from a refund restores everything); ?new=1 opens
// the creation. The page opens on the last 30 days (?period=all = « Tout »);
// `status` may be repeated (?status=approved&status=processing), as the API's.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Banknote, ChevronRight, CircleCheck, Download, ExternalLink, Hourglass, ListFilter, PieChart, Plus, RefreshCw,
  Scale, Search, Sparkles, TriangleAlert, Truck, Building2, Clock, X,
} from 'lucide-react';
import RefundCreateModal from './RefundCreateModal';
import {
  Badge, Banner, Card, EmptyState, Kpi, Meter, PageHeader, Pagination, Segmented, Tabs, Toast,
} from '../design/ui';
import { REFUND_REASONS, REFUND_SOURCES, REFUND_STATUSES } from '../utils/refunds';
import {
  LIVE_STATUSES, PERIOD_PRESETS, STATUS_TONE, amountState, breakdownRows, endOfDay, formatAmount, formatCount,
  formatDay, formatPercent, formatStamp, formatTileAmount, periodOf, presetStart, refundMoneyMetrics, statusRows,
} from './refundsMoney';
import './Refunds.css';

const MONGO_ID_RE = /^[a-f0-9]{24}$/i;
const PAGE_SIZE = 25;
const VIEWS = ['dossiers', 'repartition'];
// URL param → API param of the list, stats and export calls.
const FILTER_PARAMS = [
  ['status', 'status'], ['source', 'source'], ['reason', 'reason'], ['carrier', 'carrier'], ['client', 'client'],
  ['start', 'start_date'], ['end', 'end_date'], ['ticket', 'ticket_id'], ['order', 'order'], ['by', 'recorded_by'],
];
const LIST_FILTER_KEYS = ['status', 'source', 'reason', 'carrier', 'client', 'ticket', 'order', 'by'];
const MORE_FILTER_KEYS = ['source', 'reason', 'by', 'ticket'];
// Kevin 29/10: the page is about what is in play now — it opens on the last
// 30 days. A period in the URL (start / end, or period=all) still wins.
const DEFAULT_PERIOD_DAYS = 30;
const ALL_PERIOD = 'all';
// « À encaisser » counts the granted refunds not paid yet: it filters on both.
const TO_COLLECT_STATUSES = ['approved', 'processing'];
const SEVERAL_STATUSES = '__several';
// The settlement under an amount, in its short form when the full one runs
// past the column (the full wording stays in the cell's title).
const SETTLEMENT_SHORT = {
  monthly_credit: 'Indemnisation mensuelle',
  invoice_deduction: 'Déduit de la facture',
};

const sameSet = (a, b) => a.length === b.length && a.every((value) => b.includes(value));

const StatusBadge = ({ status, t }) => (
  <Badge tone={STATUS_TONE[status]} dot={LIVE_STATUSES.includes(status)}>
    {t(`refunds.statuses.${status}`, status)}
  </Badge>
);

// Icon-only in the table (the status column stays one line); the label is its title.
const AiBadge = ({ t }) => {
  const label = t('refunds.recordedByAiTitle', 'Enregistré par l’agent IA depuis la réponse du transporteur');
  return (
    <span className="u-badge u-badge--accent refunds-v2__ai" title={label} aria-label={label} role="img">
      <Sparkles aria-hidden="true" />
    </span>
  );
};

const Refunds = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const param = (key) => searchParams.get(key) || '';

  const view = VIEWS.includes(param('view')) ? param('view') : 'dossiers';
  const pageParam = parseInt(param('page') || '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  // One or several statuses (?status=a&status=b); a single one is the
  // historical shape every deep link uses and still reads the same.
  const statuses = searchParams.getAll('status').filter(Boolean);
  const status = statuses.length === 1 ? statuses[0] : '';
  const isOnlyStatuses = (values) => sameSet(statuses, values);
  const end = param('end');
  // No period in the URL: the default 30 days, computed, not written.
  const allTime = param('period') === ALL_PERIOD;
  const start = param('start') || (!end && !allTime ? presetStart(DEFAULT_PERIOD_DAYS) : '');
  const period = periodOf(start, end);

  // A value per key; an array is written as the key repeated.
  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        next.delete(key);
        if (Array.isArray(value)) value.filter(Boolean).forEach((item) => next.append(key, String(item)));
        else if (value !== null && value !== undefined && value !== '') next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Debounced text filters (one API call per pause, not per keystroke).
  const [carrierInput, setCarrierInput] = useState(param('carrier'));
  const [clientInput, setClientInput] = useState(param('client'));
  const [ticketInput, setTicketInput] = useState(param('ticket'));
  const [orderInput, setOrderInput] = useState(param('order'));
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const updates = {};
      if (carrierInput.trim() !== param('carrier')) updates.carrier = carrierInput.trim();
      if (clientInput.trim() !== param('client')) updates.client = clientInput.trim();
      if (orderInput.trim() !== param('order')) updates.order = orderInput.trim();
      const ticketValue = MONGO_ID_RE.test(ticketInput.trim()) ? ticketInput.trim() : '';
      if (ticketValue !== param('ticket')) updates.ticket = ticketValue;
      if (Object.keys(updates).length > 0) updateParams({ ...updates, page: null });
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierInput, clientInput, ticketInput, orderInput]);

  const [items, setItems] = useState(null); // null = loading
  const [totalItems, setTotalItems] = useState(null);
  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);
  // ?new=1 opens « Nouveau remboursement » (a link can start a creation).
  const [creating, setCreating] = useState(() => searchParams.get('new') === '1');
  const [moreOpen, setMoreOpen] = useState(() => MORE_FILTER_KEYS.some((key) => searchParams.get(key)));
  const [customOpen, setCustomOpen] = useState(false);
  const [pendingAmounts, setPendingAmounts] = useState(null); // null = not asked, undefined = loading

  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }), []);

  // What the list, the stats and the export filter on: the URL, with the
  // default period applied and every status of a repeated `status`.
  const filterValues = (key) => {
    if (key === 'start') return start ? [start] : [];
    return searchParams.getAll(key).filter(Boolean);
  };
  const filterKey = FILTER_PARAMS.map(([key]) => filterValues(key).join(',')).join('|');
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    FILTER_PARAMS.forEach(([key, apiKey]) => {
      filterValues(key).forEach((value) => params.append(apiKey, key === 'end' ? endOfDay(value) : value));
    });
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const fetchRefunds = useCallback(async () => {
    setError('');
    try {
      const params = buildFilterParams();
      params.append('page', String(page));
      params.append('limit', String(PAGE_SIZE));
      const response = await axios.get(`/api/logistics/refunds?${params.toString()}`, { headers });
      const data = response.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalItems(Number.isFinite(Number(data.total)) ? Number(data.total) : null);
    } catch (err) {
      console.error('Failed to fetch refunds:', err);
      setError(err?.response?.data?.detail || t('refunds.fetchError', 'Impossible de charger les remboursements.'));
      setItems([]);
    }
  }, [buildFilterParams, headers, page, t]);

  const fetchRefundStats = useCallback(async () => {
    try {
      const response = await axios.get(`/api/logistics/refunds/stats?${buildFilterParams().toString()}`, { headers });
      setStats(response.data || null);
    } catch (err) {
      console.error('Failed to fetch refund stats:', err);
      setStats(null);
    }
  }, [buildFilterParams, headers]);

  useEffect(() => { fetchRefunds(); }, [fetchRefunds]);
  useEffect(() => { fetchRefundStats(); }, [fetchRefundStats]);
  useEffect(() => { setPendingAmounts(null); }, [filterKey]);

  const refresh = () => { fetchRefunds(); fetchRefundStats(); setPendingAmounts(null); };

  // « À vérifier »: the granted refunds still without an amount, fetched on demand.
  const showPendingAmounts = async () => {
    setPendingAmounts(undefined);
    try {
      const params = buildFilterParams();
      if (!params.get('status')) ['approved', 'processing', 'paid'].forEach((value) => params.append('status', value));
      params.append('page', '1');
      params.append('limit', '200');
      const response = await axios.get(`/api/logistics/refunds?${params.toString()}`, { headers });
      const list = Array.isArray(response.data?.items) ? response.data.items : [];
      setPendingAmounts(list.filter((refund) => amountState(refund) === 'pending'));
    } catch (err) {
      console.error('Failed to fetch refunds without amount:', err);
      setPendingAmounts(null);
      setToast({ tone: 'danger', text: err?.response?.data?.detail || t('refunds.fetchError', 'Impossible de charger les remboursements.') });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`/api/logistics/refunds/export?${buildFilterParams().toString()}`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'refunds.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export refunds:', err);
      setToast({ tone: 'danger', text: err?.response?.data?.detail || t('refunds.exportError', 'Échec de l’export CSV.') });
    } finally {
      setExporting(false);
    }
  };

  // ------------------------------------------------------------ derived

  const metrics = useMemo(() => (stats ? refundMoneyMetrics(stats) : null), [stats]);
  const money = (amount) => formatAmount(amount, metrics?.currency, lang);
  const tileMoney = (amount) => formatTileAmount(amount, metrics?.currency, lang);
  const statsLoading = stats === undefined;
  const hasData = Boolean(metrics && metrics.count > 0);
  const listFilterCount = LIST_FILTER_KEYS.filter((key) => searchParams.get(key)).length;
  const moreFilterCount = MORE_FILTER_KEYS.filter((key) => searchParams.get(key)).length;
  const anyFilter = listFilterCount > 0 || period !== ALL_PERIOD;
  const ticketInputInvalid = ticketInput.trim() !== '' && !MONGO_ID_RE.test(ticketInput.trim());

  const periodLabel = (() => {
    if (period === 'all') return t('ordersMoneyUi.refunds.periodAll', 'depuis le premier remboursement');
    if (period !== 'custom') {
      return t('ordersMoneyUi.refunds.periodSince', 'du {{start}} à aujourd’hui', { start: formatDay(start, lang) });
    }
    if (start && end) return t('ordersMoneyUi.refunds.periodRange', 'du {{start}} au {{end}}', { start: formatDay(start, lang), end: formatDay(end, lang) });
    if (start) return t('ordersMoneyUi.refunds.periodFrom', 'depuis le {{start}}', { start: formatDay(start, lang) });
    return t('ordersMoneyUi.refunds.periodUntil', 'jusqu’au {{end}}', { end: formatDay(end, lang) });
  })();

  const setPeriod = (value) => {
    if (value === 'custom') { setCustomOpen(true); return; }
    setCustomOpen(false);
    if (value === ALL_PERIOD) updateParams({ start: null, end: null, period: ALL_PERIOD, page: null });
    else updateParams({ start: presetStart(Number(value)), end: null, period: null, page: null });
  };
  // A typed date replaces the default period (the other bound stays as shown).
  const setDate = (key, value) => updateParams({
    start, end, [key]: value, period: null, page: null,
  });

  const applyFilter = (updates) => updateParams({ ...updates, page: null, view: null });
  // A tile or a « Voir » filters on its statuses; clicking it again clears.
  const filterStatuses = (values) => applyFilter({ status: isOnlyStatuses(values) ? null : values });
  const filterStatus = (value) => filterStatuses([value]);
  const filterCarrier = (value) => { setCarrierInput(value); applyFilter({ carrier: value }); };
  const filterClient = (value) => { setClientInput(value); applyFilter({ client: value }); };
  const clearListFilters = () => {
    setCarrierInput(''); setClientInput(''); setTicketInput(''); setOrderInput('');
    updateParams(Object.fromEntries([...LIST_FILTER_KEYS.map((key) => [key, null]), ['page', null]]));
  };

  const subtitle = hasData
    ? t('ordersMoneyUi.refunds.subtitle', '{{total}} dossiers · {{granted}} accordés · {{period}}', {
      count: metrics.count, total: formatCount(metrics.count, lang), granted: money(metrics.granted), period: periodLabel,
    })
    : t('refunds.subtitle', 'Ce que les transporteurs et l’équipe accordent, par commande et par client.');

  // ------------------------------------------------------------ zones

  const renderPeriodBar = () => (
    <div className="refunds-v2__period">
      <Segmented
        label={t('ordersMoneyUi.refunds.period', 'Période')}
        value={customOpen ? 'custom' : period}
        onChange={setPeriod}
        options={[
          { value: ALL_PERIOD, label: t('ordersMoneyUi.refunds.periodAllShort', 'Tout') },
          ...PERIOD_PRESETS.map((days) => ({
            value: String(days),
            label: days === 365 ? t('ordersMoneyUi.refunds.period12m', '12 mois') : t('ordersMoneyUi.refunds.periodDays', '{{days}} jours', { days }),
          })),
          { value: 'custom', label: t('ordersMoneyUi.refunds.periodCustom', 'Dates') },
        ]}
      />
      {(customOpen || period === 'custom') && (
        <div className="refunds-v2__dates">
          <label className="refunds-v2__date">
            <span>{t('ordersMoneyUi.refunds.from', 'Du')}</span>
            <input type="date" className="u-input" value={start} max={end || undefined}
              onChange={(e) => setDate('start', e.target.value)} />
          </label>
          <label className="refunds-v2__date">
            <span>{t('ordersMoneyUi.refunds.to', 'au')}</span>
            <input type="date" className="u-input" value={end} min={start || undefined}
              onChange={(e) => setDate('end', e.target.value)} />
          </label>
        </div>
      )}
      <span className="refunds-v2__scope">
        {t('ordersMoneyUi.refunds.scope', 'Chiffres {{period}}', { period: periodLabel })}
        {listFilterCount > 0 && ` · ${t('ordersMoneyUi.refunds.scopeFiltered', 'filtres de la liste appliqués')}`}
      </span>
    </div>
  );

  const renderKpis = () => {
    if (statsLoading) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => <span key={i} className="u-skeleton refunds-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!hasData) return null;
    const m = metrics;
    const six = m.acceptanceRate !== null && m.aiCount > 0;
    return (
      <div className={`u-kpi-row${six ? ' refunds-v2__kpis--six' : ''}`}>
        <Kpi
          icon={Banknote}
          label={t('ordersMoneyUi.refunds.kpiGranted', 'Accordé')}
          value={tileMoney(m.granted)}
          title={money(m.granted)}
          context={m.amountUnknown > 0
            ? t('ordersMoneyUi.refunds.kpiGrantedUnknown', '{{count}} dossiers accordés · {{unknown}} sans montant', { count: m.grantedCount, unknown: m.amountUnknown })
            : t('ordersMoneyUi.refunds.kpiGrantedContext', '{{count}} dossiers accordés, payés ou à payer', { count: m.grantedCount })}
          contextTone={m.amountUnknown > 0 ? 'warning' : undefined}
        />
        <Kpi
          icon={CircleCheck}
          tone="success"
          label={t('ordersMoneyUi.refunds.kpiPaid', 'Récupéré')}
          value={tileMoney(m.paid)}
          title={money(m.paid)}
          context={m.paidShare !== null
            ? t('ordersMoneyUi.refunds.kpiPaidContext', '{{share}} de l’accordé · {{count}} payés', { share: formatPercent(m.paidShare, lang), count: m.paidCount })
            : t('ordersMoneyUi.refunds.kpiPaidNone', 'rien d’accordé sur la période')}
          meter={m.paidShare !== null ? { value: Math.round(m.paidShare * 100), tone: 'success', label: t('ordersMoneyUi.refunds.kpiPaid', 'Récupéré') } : undefined}
          active={isOnlyStatuses(['paid'])}
          onClick={() => filterStatus('paid')}
        />
        <Kpi
          icon={Hourglass}
          tone="warning"
          label={t('ordersMoneyUi.refunds.kpiToCollect', 'À encaisser')}
          value={tileMoney(m.toCollect)}
          title={money(m.toCollect)}
          context={t('ordersMoneyUi.refunds.kpiToCollectContext', '{{count}} accordés, pas encore payés', { count: m.toCollectCount })}
          contextTone={m.toCollectCount > 0 ? 'warning' : undefined}
          active={isOnlyStatuses(TO_COLLECT_STATUSES)}
          onClick={() => filterStatuses(TO_COLLECT_STATUSES)}
        />
        <Kpi
          icon={Clock}
          tone="info"
          label={t('ordersMoneyUi.refunds.kpiRequested', 'Sans décision')}
          value={formatCount(m.requestedCount, lang)}
          context={t('ordersMoneyUi.refunds.kpiRequestedContext', 'demandes en attente du transporteur')}
          active={isOnlyStatuses(['requested'])}
          onClick={() => filterStatus('requested')}
        />
        {m.acceptanceRate !== null && (
          <Kpi
            icon={Scale}
            tone={m.acceptanceRate >= 0.5 ? 'success' : 'danger'}
            label={t('ordersMoneyUi.refunds.kpiAcceptance', 'Taux d’acceptation')}
            value={formatPercent(m.acceptanceRate, lang)}
            context={t('ordersMoneyUi.refunds.kpiAcceptanceContext', '{{granted}} accordés · {{rejected}} refusés', { granted: m.grantedCount, rejected: m.rejectedCount })}
            meter={{ value: Math.round(m.acceptanceRate * 100), tone: m.acceptanceRate >= 0.5 ? 'success' : 'danger', label: t('ordersMoneyUi.refunds.kpiAcceptance', 'Taux d’acceptation') }}
            active={isOnlyStatuses(['rejected'])}
            onClick={() => filterStatus('rejected')}
            title={t('ordersMoneyUi.refunds.kpiAcceptanceTitle', 'Voir les refus')}
          />
        )}
        {m.aiCount > 0 && (
          <Kpi
            icon={Sparkles}
            label={t('ordersMoneyUi.refunds.kpiAi', 'Enregistrés par l’IA')}
            value={formatPercent(m.aiShare, lang)}
            context={t('ordersMoneyUi.refunds.kpiAiContext', '{{count}} sur {{total}} dossiers', { count: m.aiCount, total: m.count })}
            active={param('by') === 'ai'}
            onClick={() => applyFilter({ by: param('by') === 'ai' ? null : 'ai' })}
          />
        )}
      </div>
    );
  };

  const renderAttention = () => {
    if (!hasData || metrics.amountUnknown <= 0) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('ordersMoneyUi.attention', 'À vérifier')}
        badge={<Badge tone="warning">1</Badge>}
        flush
        className="refunds-v2__attention"
      >
        <ul className="u-list">
          <li className="u-row">
            <Badge tone="warning" dot>{t('ordersMoneyUi.toComplete', 'À compléter')}</Badge>
            <div className="u-row__main">
              <div className="u-row__title">
                {t('ordersMoneyUi.refunds.attentionUnknown', '{{count}} remboursements accordés sans montant', { count: metrics.amountUnknown })}
              </div>
              <div className="u-row__meta u-row__meta--wrap">
                {t('ordersMoneyUi.refunds.attentionUnknownHint', 'Le transporteur a accepté mais le montant n’est pas saisi : ces dossiers ne comptent pas dans les montants.')}
              </div>
            </div>
            <div className="u-row__side">
              {pendingAmounts ? (
                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setPendingAmounts(null)}>
                  {t('ordersMoneyUi.hide', 'Masquer')}
                </button>
              ) : (
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={showPendingAmounts} disabled={pendingAmounts === undefined}>
                  {pendingAmounts === undefined ? t('ordersMoneyUi.loading', 'Chargement…') : t('ordersMoneyUi.see', 'Voir')}
                  <ChevronRight aria-hidden="true" />
                </button>
              )}
            </div>
          </li>
          {Array.isArray(pendingAmounts) && pendingAmounts.map((refund) => (
            <li className="u-row refunds-v2__attention-item" key={refund._id}>
              <StatusBadge status={refund.status} t={t} />
              <div className="u-row__main">
                <div className="u-row__title">
                  {[refund.carrier_name, refund.order_reference && t('refunds.orderShort', 'Cde {{order}}', { order: refund.order_reference })].filter(Boolean).join(' · ')
                    || refund.external_ticket_id || refund._id.slice(-8)}
                </div>
                <div className="u-row__meta">
                  {[refund.client_name || refund.merchant_name, formatStamp(refund.created_at, lang)].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="u-row__side">
                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => navigate(`/refunds/${refund._id}`)}>
                  {t('ordersMoneyUi.refunds.addAmount', 'Saisir le montant')}
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
          {Array.isArray(pendingAmounts) && pendingAmounts.length === 0 && (
            <li className="u-row refunds-v2__attention-item">
              <span className="u-muted">{t('ordersMoneyUi.refunds.attentionNoneFound', 'Ces dossiers ne sont plus dans la liste filtrée.')}</span>
            </li>
          )}
        </ul>
      </Card>
    );
  };

  const renderToolbar = () => (
    <>
      <div className="u-toolbar refunds-v2__toolbar">
        <div className="u-input-group u-input-group--lead">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            className="u-input"
            type="search"
            value={orderInput}
            onChange={(e) => setOrderInput(e.target.value)}
            placeholder={t('refunds.orderPlaceholder', 'Référence de commande')}
            aria-label={t('refunds.orderReference', 'Commande')}
          />
        </div>
        <input
          className="u-input refunds-v2__text-filter"
          value={carrierInput}
          onChange={(e) => setCarrierInput(e.target.value)}
          placeholder={t('refunds.carrierName', 'Transporteur')}
          aria-label={t('refunds.carrierName', 'Transporteur')}
        />
        <input
          className="u-input refunds-v2__text-filter"
          value={clientInput}
          onChange={(e) => setClientInput(e.target.value)}
          placeholder={t('refunds.clientPlaceholder', 'Client ou marchand')}
          aria-label={t('refunds.clientName', 'Client')}
        />
        <select
          className="u-select"
          value={statuses.length > 1 ? SEVERAL_STATUSES : status}
          onChange={(e) => { if (e.target.value !== SEVERAL_STATUSES) applyFilter({ status: e.target.value }); }}
          aria-label={t('refunds.status', 'Statut')}
        >
          <option value="">{t('refunds.allStatuses', 'Tous les statuts')}</option>
          {statuses.length > 1 && (
            <option value={SEVERAL_STATUSES}>
              {statuses.map((value) => t(`refunds.statuses.${value}`, value)).join(' + ')}
            </option>
          )}
          {REFUND_STATUSES.map((value) => (
            <option key={value} value={value}>{t(`refunds.statuses.${value}`, value)}</option>
          ))}
        </select>
        <button
          type="button"
          className="u-btn u-btn--ghost u-btn--sm"
          onClick={() => setMoreOpen((open) => !open)}
          aria-expanded={moreOpen}
        >
          <ListFilter aria-hidden="true" />
          {t('ordersMoneyUi.moreFilters', 'Plus de filtres')}
          {moreFilterCount > 0 && <Badge tone="accent">{moreFilterCount}</Badge>}
        </button>
        <span className="u-toolbar__spacer" />
        {listFilterCount > 0 && (
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={clearListFilters}>
            <X aria-hidden="true" />
            {t('ordersMoneyUi.clearFilters', 'Effacer les filtres')}
          </button>
        )}
      </div>
      {moreOpen && (
        <div className="refunds-v2__more">
          <label className="u-field">
            <span className="u-label">{t('refunds.source', 'Source')}</span>
            <select className="u-select" value={param('source')} onChange={(e) => applyFilter({ source: e.target.value })}>
              <option value="">{t('refunds.allSources', 'Toutes les sources')}</option>
              {REFUND_SOURCES.map((value) => <option key={value} value={value}>{t(`refunds.sources.${value}`, value)}</option>)}
            </select>
          </label>
          <label className="u-field">
            <span className="u-label">{t('refunds.reason', 'Motif')}</span>
            <select className="u-select" value={param('reason')} onChange={(e) => applyFilter({ reason: e.target.value })}>
              <option value="">{t('refunds.allReasons', 'Tous les motifs')}</option>
              {REFUND_REASONS.map((value) => <option key={value} value={value}>{t(`refunds.reasons.${value}`, value)}</option>)}
            </select>
          </label>
          <label className="u-field">
            <span className="u-label">{t('refunds.recordedBy', 'Enregistré par')}</span>
            <select className="u-select" value={param('by')} onChange={(e) => applyFilter({ by: e.target.value })}>
              <option value="">{t('refunds.recordedByAll', 'Tous')}</option>
              <option value="ai">{t('refunds.recordedByAiOption', 'Agent IA')}</option>
              <option value="human">{t('refunds.recordedByTeam', 'Équipe')}</option>
            </select>
          </label>
          <label className="u-field">
            <span className="u-label">{t('refunds.ticketId', 'Ticket')}</span>
            <input
              className="u-input"
              value={ticketInput}
              onChange={(e) => setTicketInput(e.target.value)}
              placeholder={t('ordersMoneyUi.refunds.ticketPlaceholder', 'Identifiant du ticket (24 caractères)')}
              aria-invalid={ticketInputInvalid ? 'true' : undefined}
            />
            {ticketInputInvalid && <span className="u-error-text">{t('refunds.ticketIdHint', 'Identifiant complet requis (24 caractères)')}</span>}
          </label>
        </div>
      )}
    </>
  );

  const renderAmount = (refund) => {
    const state = amountState(refund);
    if (state === 'amount') return formatAmount(refund.total_refund_amount, refund.currency || 'EUR', lang);
    if (state === 'pending') {
      return (
        <span className="refunds-v2__pending" title={t('refunds.amountToConfirm', 'Montant à confirmer')}>
          {t('ordersMoneyUi.detail.toConfirm', 'À confirmer')}
        </span>
      );
    }
    return <span className="u-muted">—</span>;
  };

  const renderTable = () => {
    if (items === null) {
      return (
        <div className="refunds-v2__skeleton" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (items.length === 0) {
      // Only the period narrows the list (the 30-day default included): say so
      // and offer the whole history, which may hold older refunds.
      if (anyFilter && listFilterCount === 0) {
        return (
          <EmptyState
            icon={Search}
            title={t('listsPolishUi.refunds.emptyPeriod', 'Aucun remboursement {{period}}', { period: periodLabel })}
            action={(
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setPeriod(ALL_PERIOD)}>
                {t('listsPolishUi.refunds.showAllPeriod', 'Voir toute la période')}
              </button>
            )}
          >
            {t('ordersMoneyUi.refunds.emptyFilteredHint', 'Changez la période ou les filtres pour voir d’autres dossiers.')}
          </EmptyState>
        );
      }
      return anyFilter ? (
        <EmptyState
          icon={Search}
          title={t('ordersMoneyUi.refunds.emptyFiltered', 'Aucun remboursement ne correspond')}
          action={listFilterCount > 0 && (
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={clearListFilters}>
              {t('ordersMoneyUi.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        >
          {t('ordersMoneyUi.refunds.emptyFilteredHint', 'Changez la période ou les filtres pour voir d’autres dossiers.')}
        </EmptyState>
      ) : (
        <EmptyState
          icon={Banknote}
          title={t('refunds.emptyAll', 'Aucun remboursement enregistré pour l’instant.')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setCreating(true)}>
              <Plus aria-hidden="true" />
              {t('refunds.newRefund', 'Nouveau remboursement')}
            </button>
          )}
        >
          {t('refunds.emptyAllHint', 'L’agent IA enregistre ici les décisions de remboursement des transporteurs dès leur réponse ; vous pouvez aussi en saisir un vous-même.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table refunds-v2__table">
          <thead>
            <tr>
              {/* Montant 19 %: the amount over its settlement (« Indemnisation
                  mensuelle », « Déduit de la facture ») on one line at 1 440. */}
              <th style={{ width: '12%' }}>{t('refunds.ticket', 'Ticket')}</th>
              <th style={{ width: '15%' }}>{t('refunds.clientName', 'Client')}</th>
              <th style={{ width: '13%' }}>{t('refunds.carrierName', 'Transporteur')}</th>
              <th style={{ width: '12%' }}>{t('refunds.reason', 'Motif')}</th>
              <th style={{ width: '19%' }}>{t('refunds.status', 'Statut')}</th>
              <th style={{ width: '19%' }} className="refunds-v2__num">{t('refunds.amount', 'Montant')}</th>
              <th style={{ width: '10%' }}>{t('refunds.createdAt', 'Créé le')}</th>
              <th style={{ width: '62px' }} aria-label={t('ordersMoneyUi.open', 'Ouvrir')} />
            </tr>
          </thead>
          <tbody>
            {items.map((refund) => {
              const client = refund.client_name || refund.merchant_name;
              const settlement = refund.settlement && refund.settlement !== 'unknown' && amountState(refund) !== 'none'
                ? t(`refunds.settlements.${refund.settlement}`, refund.settlement) : '';
              const settlementShort = settlement && SETTLEMENT_SHORT[refund.settlement]
                ? t(`listsPolishUi.refunds.settlementShort.${refund.settlement}`, SETTLEMENT_SHORT[refund.settlement])
                : settlement;
              return (
                <tr
                  key={refund._id}
                  className="refunds-v2__row"
                  tabIndex={0}
                  onClick={() => navigate(`/refunds/${refund._id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/refunds/${refund._id}`); }}
                >
                  <td>
                    {refund.ticket_id ? (
                      <button
                        type="button"
                        className="refunds-v2__ticket"
                        title={t('refunds.openTicket', 'Ouvrir le ticket')}
                        onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${refund.ticket_id}/messages`); }}
                      >
                        {refund.external_ticket_id || refund.ticket_id.slice(-8)}
                        <ExternalLink aria-hidden="true" />
                      </button>
                    ) : <span className="u-muted">—</span>}
                    {refund.order_reference && (
                      <span className="u-cell-sub">{t('refunds.orderShort', 'Cde {{order}}', { order: refund.order_reference })}</span>
                    )}
                  </td>
                  <td title={client || ''}>
                    {client || <span className="u-muted">—</span>}
                    {refund.client_id && <span className="u-cell-sub">{refund.client_id}</span>}
                  </td>
                  <td title={[refund.carrier_name, refund.tracking_number].filter(Boolean).join(' · ')}>
                    {refund.carrier_name || <span className="u-muted">—</span>}
                    {refund.tracking_number && <span className="u-cell-sub refunds-v2__mono">{refund.tracking_number}</span>}
                  </td>
                  <td>
                    {refund.reason ? t(`refunds.reasons.${refund.reason}`, refund.reason) : <span className="u-muted">—</span>}
                    {refund.source && <span className="u-cell-sub">{t(`refunds.sources.${refund.source}`, refund.source)}</span>}
                  </td>
                  <td>
                    <span className="refunds-v2__badges">
                      <StatusBadge status={refund.status} t={t} />
                      {refund.created_by_kind === 'ai' && <AiBadge t={t} />}
                    </span>
                  </td>
                  <td className="refunds-v2__num" title={settlement || undefined}>
                    {renderAmount(refund)}
                    {settlementShort && <span className="u-cell-sub">{settlementShort}</span>}
                  </td>
                  <td>{formatStamp(refund.created_at, lang)}</td>
                  <td className="refunds-v2__chevron"><ChevronRight aria-hidden="true" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDossiers = () => (
    <section className="u-card">
      {renderToolbar()}
      {renderTable()}
      {items && items.length > 0 && totalItems !== null && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={totalItems}
          onChange={(next) => updateParams({ page: next <= 1 ? null : next })}
          summary={({ first, last, total }) => t('ordersMoneyUi.pageSummary', '{{first}}–{{last}} sur {{total}}', {
            first, last, total: formatCount(total, lang),
          })}
          previousLabel={t('ordersMoneyUi.previousPage', 'Page précédente')}
          nextLabel={t('ordersMoneyUi.nextPage', 'Page suivante')}
        />
      )}
    </section>
  );

  const renderBreakdown = (title, icon, container, onPick, emptyLabel) => {
    const { rows, others, max } = breakdownRows(container, 8);
    if (!rows.length) return null;
    return (
      <Card
        icon={icon}
        title={title}
        description={metrics?.multiCurrency
          ? t('ordersMoneyUi.refunds.breakdownMixed', 'Montants toutes devises confondues · cliquez une ligne pour filtrer les dossiers')
          : t('ordersMoneyUi.refunds.breakdownHint', 'Accordé = approuvé, en cours de paiement ou payé · cliquez une ligne pour filtrer les dossiers')}
        flush
      >
        <div className="u-table-scroll">
          <table className="u-table refunds-v2__breakdown">
            <thead>
              <tr>
                <th style={{ width: '26%' }}>{title}</th>
                <th style={{ width: '12%' }} className="refunds-v2__num">{t('ordersMoneyUi.refunds.colDossiers', 'Dossiers')}</th>
                <th style={{ width: '30%' }}>{t('ordersMoneyUi.refunds.colGranted', 'Accordé')}</th>
                <th style={{ width: '14%' }} className="refunds-v2__num">{t('ordersMoneyUi.refunds.colPaid', 'Payé')}</th>
                <th style={{ width: '18%' }}>{t('ordersMoneyUi.refunds.colAcceptance', 'Acceptation')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.label}
                  className={row.unknown ? undefined : 'refunds-v2__row'}
                  tabIndex={row.unknown ? undefined : 0}
                  onClick={row.unknown ? undefined : () => onPick(row.label)}
                  onKeyDown={row.unknown ? undefined : (e) => { if (e.key === 'Enter') onPick(row.label); }}
                  title={row.unknown ? undefined : t('ordersMoneyUi.refunds.filterOn', 'Filtrer les dossiers sur {{name}}', { name: row.label })}
                >
                  <td>{row.unknown ? <span className="u-muted">{emptyLabel}</span> : row.label}</td>
                  <td className="refunds-v2__num">{formatCount(row.count, lang)}</td>
                  <td>
                    <span className="refunds-v2__bar-cell">
                      <span className="refunds-v2__bar-value">{money(row.granted)}</span>
                      <Meter value={row.granted} max={max || 1} label={money(row.granted)} />
                    </span>
                  </td>
                  <td className="refunds-v2__num">{row.paid > 0 ? money(row.paid) : <span className="u-muted">—</span>}</td>
                  <td>
                    {row.acceptanceRate !== null ? (
                      <span className="refunds-v2__rate" title={t('ordersMoneyUi.refunds.kpiAcceptanceContext', '{{granted}} accordés · {{rejected}} refusés', { granted: row.grantedCount, rejected: row.rejectedCount })}>
                        {formatPercent(row.acceptanceRate, lang)}
                        <span className="u-cell-sub">
                          {t('ordersMoneyUi.refunds.rejectedShort', '{{count}} refusés', { count: row.rejectedCount })}
                        </span>
                      </span>
                    ) : <span className="u-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {others && (
          <div className="refunds-v2__others">
            {t('ordersMoneyUi.refunds.others', '+ {{labels}} autres : {{total}} dossiers, {{granted}} accordés', {
              labels: others.labels, count: others.count, total: formatCount(others.count, lang), granted: money(others.granted),
            })}
          </div>
        )}
      </Card>
    );
  };

  const renderStatusCard = () => {
    const rows = statusRows(stats);
    const grantedRows = rows.filter((row) => row.granted && row.amount > 0);
    const grantedTotal = grantedRows.reduce((sum, row) => sum + row.amount, 0);
    return (
      <Card
        icon={Banknote}
        title={t('ordersMoneyUi.refunds.statusTitle', 'Où en est l’argent')}
        description={t('ordersMoneyUi.refunds.statusDesc', 'Les dossiers et leurs montants à chaque étape, {{period}}.', { period: periodLabel })}
        flush
      >
        {grantedTotal > 0 && (
          <div className="refunds-v2__pad">
            <div className="u-stack" role="img" aria-label={grantedRows.map((row) => `${t(`refunds.statuses.${row.status}`, row.status)} ${money(row.amount)}`).join(', ')}>
              {grantedRows.map((row) => (
                <span key={row.status} className={`refunds-v2__seg refunds-v2__seg--${row.status}`} style={{ width: `${(row.amount / grantedTotal) * 100}%` }} />
              ))}
            </div>
          </div>
        )}
        <ul className="u-list">
          {rows.filter((row) => row.count > 0).map((row) => (
            <li className="u-row refunds-v2__status-row" key={row.status}>
              <span className={`refunds-v2__swatch refunds-v2__seg--${row.status}`} aria-hidden="true" />
              <div className="u-row__main">
                <div className="u-row__title">{t(`refunds.statuses.${row.status}`, row.status)}</div>
                <div className="u-row__meta">{t('ordersMoneyUi.refunds.dossiers', '{{count}} dossiers', { count: row.count })}</div>
              </div>
              <span className="refunds-v2__status-amount">{row.amount > 0 ? money(row.amount) : <span className="u-muted">—</span>}</span>
              <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => filterStatus(row.status)}>
                {t('ordersMoneyUi.see', 'Voir')}
                <ChevronRight aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderCompositionCard = () => {
    const m = metrics;
    const total = m.composition.reduce((sum, part) => sum + part.value, 0);
    const labels = {
      shipping: t('refunds.shippingAmount', 'Transport'),
      content: t('refunds.contentAmount', 'Contenu'),
      insurance: t('refunds.insuranceAmount', 'Assurance'),
      other: t('refunds.otherAmount', 'Autre'),
    };
    return (
      <Card
        icon={PieChart}
        title={t('ordersMoneyUi.refunds.compositionTitle', 'Ce qui est remboursé')}
        description={t('ordersMoneyUi.refunds.compositionDesc', 'Le montant accordé, par nature.')}
      >
        <div className="refunds-v2__composition">
          {total > 0 ? (
            <>
              <div className="u-stack" role="img" aria-label={t('refunds.compositionAria', 'Répartition du montant remboursé')}>
                {m.composition.map((part, index) => (
                  <span key={part.key} className={`refunds-v2__series-${index + 1}`} style={{ width: `${(part.value / total) * 100}%` }} />
                ))}
              </div>
              <div className="u-legend">
                {m.composition.map((part, index) => (
                  <span key={part.key} className="u-legend__item">
                    <span className={`u-legend__swatch refunds-v2__series-${index + 1}`} />
                    {labels[part.key]} · {money(part.value)}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <p className="u-muted refunds-v2__note">{t('ordersMoneyUi.refunds.compositionNone', 'Aucun montant accordé sur la période.')}</p>
          )}
          <dl className="u-dl">
            {m.average > 0 && (
              <div>
                <dt>{t('ordersMoneyUi.refunds.average', 'Remboursement moyen')}</dt>
                <dd>{money(m.average)}</dd>
              </div>
            )}
            <div>
              <dt>{t('ordersMoneyUi.refunds.insured', 'Dossiers assurés')}</dt>
              <dd>{formatCount(m.insuredCount, lang)}</dd>
            </div>
            {m.insurancePrice > 0 && (
              <div>
                <dt>{t('ordersMoneyUi.refunds.premiums', 'Primes d’assurance')}</dt>
                <dd>
                  {money(m.insurancePrice)}
                  <span className="u-cell-sub">{t('refunds.kpiInsurancePriceHint', 'coût, pas un remboursement')}</span>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </Card>
    );
  };

  const renderRepartition = () => {
    if (statsLoading) {
      return <div className="u-grid-2" aria-hidden="true"><span className="u-skeleton refunds-v2__card-skeleton" /><span className="u-skeleton refunds-v2__card-skeleton" /></div>;
    }
    if (!hasData) {
      return (
        <section className="u-card">
          <EmptyState icon={PieChart} title={t('ordersMoneyUi.refunds.noRepartition', 'Rien à répartir sur la période')}>
            {t('ordersMoneyUi.refunds.noRepartitionHint', 'Les montants par statut, transporteur et client apparaissent dès le premier remboursement.')}
          </EmptyState>
        </section>
      );
    }
    return (
      <div className="refunds-v2__stack">
        <div className="u-grid-2 refunds-v2__grid">
          {renderStatusCard()}
          {renderCompositionCard()}
        </div>
        {renderBreakdown(t('refunds.carrierName', 'Transporteur'), Truck, stats.by_carrier, filterCarrier, t('ordersMoneyUi.refunds.noCarrier', 'Transporteur non renseigné'))}
        {renderBreakdown(t('refunds.clientName', 'Client'), Building2, stats.by_client, filterClient, t('ordersMoneyUi.refunds.noClient', 'Client non renseigné'))}
      </div>
    );
  };

  return (
    <div className="u-page refunds-v2">
      <PageHeader
        title={t('refunds.title', 'Remboursements')}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={refresh}
              title={t('refunds.refresh', 'Actualiser')}
              aria-label={t('refunds.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button type="button" className="u-btn u-btn--secondary" onClick={handleExport} disabled={exporting}>
              <Download aria-hidden="true" />
              {exporting ? t('refunds.exporting', 'Export…') : t('refunds.exportCSV', 'Exporter CSV')}
            </button>
            <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
              <Plus aria-hidden="true" />
              {t('refunds.newRefund', 'Nouveau remboursement')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {renderPeriodBar()}
        {error && <Banner tone="danger">{error}</Banner>}
        {metrics?.multiCurrency && (
          <Banner tone="info">
            {t('ordersMoneyUi.refunds.multiCurrency', 'Montants en {{currency}} uniquement : les remboursements en {{others}} sont comptés dans les dossiers, pas dans les montants.', {
              currency: metrics.currency, others: metrics.otherCurrencies.join(', '),
            })}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        <Tabs
          label={t('refunds.title', 'Remboursements')}
          active={view}
          onChange={(next) => updateParams({ view: next === 'dossiers' ? null : next })}
          tabs={[
            { key: 'dossiers', label: t('ordersMoneyUi.refunds.tabDossiers', 'Dossiers'), count: totalItems !== null ? formatCount(totalItems, lang) : undefined },
            { key: 'repartition', label: t('ordersMoneyUi.refunds.tabRepartition', 'Répartition') },
          ]}
        />
        <div role="tabpanel">
          {view === 'dossiers' ? renderDossiers() : renderRepartition()}
        </div>
      </div>

      {creating && (
        <RefundCreateModal
          onClose={() => { setCreating(false); updateParams({ new: null }); }}
          onCreated={(refund) => { setCreating(false); navigate(`/refunds/${refund._id}`); }}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default Refunds;
